import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShieldCheck, Shield, Radio, Database } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Tiny one-glance health strip for the top of admin pages.
 *
 *   🟢 Resend  ·  🟢 Retencja  ·  🟢 LLM
 *
 * Each dot is clickable and routes to the relevant diagnostic. The strip
 * never performs live third-party calls — it reads from the cached admin
 * health endpoints, so refreshes are free.
 *
 * Status palette:
 *   - "ok"     — green   (everything green-light)
 *   - "warn"   — yellow  (configured but degraded / not yet observed)
 *   - "down"   — red     (misconfigured or last call failed)
 *   - "unknown"— grey    (initial fetch in flight or endpoint unreachable)
 */
const DOT_COLORS = {
  ok: '#00D4AA',
  warn: '#F5C542',
  down: '#FF5C5C',
  unknown: '#6B7280',
};

const DOT_LABELS_PL = {
  ok: 'OK',
  warn: 'do sprawdzenia',
  down: 'awaria',
  unknown: 'sprawdzam…',
};

const Dot = ({ status }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block',
      width: 9,
      height: 9,
      borderRadius: '50%',
      background: DOT_COLORS[status] || DOT_COLORS.unknown,
      boxShadow: `0 0 0 2px ${DOT_COLORS[status] || DOT_COLORS.unknown}22`,
    }}
  />
);

const Pill = ({ to, status, label, testid, title }) => (
  <Link
    to={to}
    data-testid={testid}
    title={title || `${label}: ${DOT_LABELS_PL[status] || status}`}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '6px 12px',
      borderRadius: 999,
      border: '1px solid rgba(255,255,255,0.06)',
      background: 'rgba(255,255,255,0.02)',
      color: 'var(--hussar-text-secondary, #9aa3ad)',
      fontSize: 12,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
    }}
  >
    <Dot status={status} />
    <span>{label}</span>
    <span
      style={{ color: DOT_COLORS[status] || DOT_COLORS.unknown, fontWeight: 600 }}
      data-testid={`${testid}-status`}
    >
      {DOT_LABELS_PL[status] || status}
    </span>
  </Link>
);

const AdminStatusStrip = () => {
  const [resend, setResend] = useState('unknown');
  const [retention, setRetention] = useState('unknown');
  const [llm, setLlm] = useState('unknown');
  const [intel, setIntel] = useState({ status: 'unknown', label_pl: 'Intel', label_en: 'Intel' });
  const [security, setSecurity] = useState({ status: 'unknown', label_pl: 'Security', label_en: 'Security' });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [r1, r2, r3, r4, r5] = await Promise.allSettled([
      axios.get(`${API}/admin/resend/health`, { withCredentials: true }),
      axios.get(`${API}/admin/data-governance/scheduler-status`, { withCredentials: true }),
      axios.get(`${API}/admin/llm/health`, { withCredentials: true }),
      axios.get(`${API}/admin/intel/strip-status`, { withCredentials: true }),
      axios.get(`${API}/admin/security/strip-status`, { withCredentials: true }),
    ]);

    // Resend
    if (r1.status === 'fulfilled') {
      const d = r1.value.data || {};
      if (!d.api_key_present) setResend('down');
      else if (d.last_send_status === 'sent') setResend('ok');
      else if (d.last_send_status) setResend('warn');
      else setResend('warn'); // key present, never sent yet
    } else {
      setResend('down');
    }

    // Retention scheduler
    if (r2.status === 'fulfilled') {
      const d = r2.value.data || {};
      if (d.enabled && d.running) setRetention('ok');
      else if (d.enabled) setRetention('warn');
      else setRetention('down');
    } else {
      setRetention('down');
    }

    // LLM
    if (r3.status === 'fulfilled') {
      const d = r3.value.data || {};
      setLlm(d.api_key_present ? 'ok' : 'down');
    } else {
      setLlm('down');
    }

    // Intel
    if (r4.status === 'fulfilled') {
      const d = r4.value.data || {};
      setIntel({
        status: d.status || 'unknown',
        label_pl: d.label_pl || 'Intel',
        label_en: d.label_en || 'Intel',
      });
    } else {
      setIntel({ status: 'down', label_pl: 'Intel · niedostępny', label_en: 'Intel · unavailable' });
    }

    // Security
    if (r5.status === 'fulfilled') {
      const d = r5.value.data || {};
      setSecurity({
        status: d.status || 'unknown',
        label_pl: d.label_pl || 'Security',
        label_en: d.label_en || 'Security',
      });
    } else {
      setSecurity({ status: 'down', label_pl: 'Security · niedostępny', label_en: 'Security · unavailable' });
    }

    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <>
    <div
      data-testid="admin-status-strip"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        margin: '12px 0 6px',
      }}
    >
      <Pill
        to="/admin/data-governance"
        status={resend}
        label="Resend"
        testid="admin-strip-resend"
      />
      <Pill
        to="/admin/data-governance"
        status={retention}
        label="Retencja"
        testid="admin-strip-retention"
      />
      <Pill
        to="/admin/data-governance"
        status={llm}
        label="LLM"
        testid="admin-strip-llm"
      />
      <Pill
        to="/admin/immigration-intel"
        status={intel.status}
        label="Intel"
        testid="admin-strip-intel"
        title={intel.label_pl}
      />
      <Pill
        to="/admin/security-events"
        status={security.status}
        label="Security"
        testid="admin-strip-security"
        title={security.label_pl}
      />
      {/* Legend — explains what triggers red / yellow / green on the Security
          pill (and by extension every dot). Uses a native <details> so it
          needs no JS state and is keyboard-accessible. Text-only. */}
      <details
        data-testid="admin-strip-legend"
        style={{
          display: 'inline-block',
          fontSize: 11,
          color: '#94A3B8',
          marginLeft: 4,
        }}
      >
        <summary
          data-testid="admin-strip-legend-summary"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 8px',
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(255,255,255,0.02)',
            color: '#94A3B8',
            cursor: 'pointer',
            listStyle: 'none',
          }}
          title="Legenda statusów · Status legend"
        >
          <span aria-hidden="true">ℹ️</span>
          <span>Legenda · Legend</span>
        </summary>
        <div
          role="note"
          style={{
            marginTop: 8,
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(15,23,42,0.9)',
            color: '#CBD5F5',
            fontSize: 11,
            lineHeight: 1.5,
            maxWidth: 460,
          }}
        >
          <div style={{ marginBottom: 6, color: '#F1F5F9', fontWeight: 600 }}>
            Co oznaczają kolory? · What the dots mean
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '16px 1fr', gap: '6px 8px', alignItems: 'start' }}>
            <span style={{ color: DOT_COLORS.down }}>●</span>
            <span>
              <strong style={{ color: '#FCA5A5' }}>Czerwony · Red:</strong>{' '}
              odnotowano ostatnio zdarzenie o wysokim lub krytycznym poziomie —
              recent high or critical security event.
            </span>
            <span style={{ color: DOT_COLORS.warn }}>●</span>
            <span>
              <strong style={{ color: '#FCD34D' }}>Żółty · Yellow:</strong>{' '}
              zdarzenia wymagające przeglądu — events requiring review.
            </span>
            <span style={{ color: DOT_COLORS.ok }}>●</span>
            <span>
              <strong style={{ color: '#34D399' }}>Zielony · Green:</strong>{' '}
              brak ostatnich zdarzeń wywołujących przegląd — no recent
              review-triggering events.
            </span>
          </div>
          <div style={{ marginTop: 10, color: '#94A3B8', fontStyle: 'italic' }}>
            Status uwzględnia zdarzenia z ostatnich 24 godzin i niekoniecznie
            oznacza aktywną awarię. · Status reflects events from the last 24 h
            and does not necessarily indicate an active outage.
          </div>
        </div>
      </details>
      {loading && (
        <span data-testid="admin-strip-loading"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#6B7280', fontSize: 11 }}>
          <Loader2 className="w-3 h-3 animate-spin" />
        </span>
      )}
      </div>

      {/* Admin-only navigation row: Ochrona / Protection cluster.
          Only renders inside <AdminRoute> wrapped pages so non-admins can't
          discover these routes from public surface. */}
      <nav
        data-testid="admin-nav-protection"
        aria-label="Ochrona / Protection"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
          margin: '0 0 12px',
          padding: '8px 10px',
          borderRadius: 8,
          border: '1px solid rgba(125, 211, 252, 0.08)',
          background: 'rgba(125, 211, 252, 0.03)',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
            textTransform: 'uppercase', color: '#7DD3FC',
            paddingRight: 4, borderRight: '1px solid rgba(255,255,255,0.08)',
            marginRight: 4,
          }}
        >
          Ochrona · Protection
        </span>
        <NavLink to="/admin/security-events" testid="admin-nav-security-events" Icon={Shield}>
          Security Events
        </NavLink>
        <NavLink to="/admin/immigration-intel" testid="admin-nav-immigration-intel" Icon={Radio}>
          Immigration Intel
        </NavLink>
        <NavLink to="/admin/authenticity-shield" testid="admin-nav-authenticity-shield" Icon={ShieldCheck}>
          Tarcza Autentyczności · Authenticity Shield
        </NavLink>
        <NavLink to="/admin/data-governance" testid="admin-nav-data-governance" Icon={Database}>
          Data Governance
        </NavLink>
      </nav>
    </>
  );
};

// Small inline component — kept inside the file so AdminStatusStrip stays
// the only export. Used only by the Ochrona nav row.
const NavLink = ({ to, testid, Icon, children }) => (
  <Link
    to={to}
    data-testid={testid}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '5px 10px',
      borderRadius: 6,
      fontSize: 12,
      textDecoration: 'none',
      color: 'var(--hussar-text-secondary, #cbd5e1)',
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)',
      whiteSpace: 'nowrap',
    }}
  >
    {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
    {children}
  </Link>
);

export default AdminStatusStrip;
